import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useSubsidiariesApi } from "../../http/subsidiaries/api";
import { Subsidiary } from "../../http/subsidiaries/types";
import FileUploader from "../ui/FileUploader";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import reactSelectStyles from "../ui/ReactSelectUtils";

const SUBSIDIARIES_FIELDS = ["id", "name"] as const;
type SubsidiaryField = (typeof SUBSIDIARIES_FIELDS)[number];
type SubsidiarySearch = Pick<Subsidiary, SubsidiaryField>;

export interface CostsImportFormBody {
  fileCsv: File;
  type: TypeOf;
  subsidiary: Subsidiary;
}

export type TypeOf = {
  value: "dkv" | "tolls";
  label: string;
};

type CostsImportFormProps = {
  onSubmit: (form: CostsImportFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const CostsImportForm: FC<CostsImportFormProps> = ({ onSubmit, isDisabled, isLoading }: CostsImportFormProps) => {
  const i18n = useI18n();
  const subsidiaryApi = useSubsidiariesApi();

  const [defaultSubsidiaryOptions, setDefaultSubsidiaryOptions] = useState<SubsidiarySearch[]>([]);

  const typesOf: TypeOf[] = [
    { value: "dkv", label: "Dkv" },
    { value: "tolls", label: i18n.t.common.tolls },
  ];

  return (
    <Form<CostsImportFormBody>
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => (formData) => onSubmit(formData)}
    >
      {({ control, setValue }) => {
        return (
          <FormFieldsContainer>
            <Controller
              name="subsidiary"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.subsidiaries.singular} error={fieldState.error}>
                  {({ labelId, isInvalid }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions<SubsidiarySearch, SubsidiaryField>(
                      aborter,
                      searchTerm,
                      subsidiaryApi.getAdminSubsidiaries,
                      { fields: SUBSIDIARIES_FIELDS },
                    );

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultSubsidiaryOptions);
                    }, [searchTerm]);

                    return (
                      <AsyncSelect
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultSubsidiaryOptions}
                        loadOptions={loadOptions}
                        isDisabled={isLoading || isDisabled}
                        isLoading={isLoading}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        styles={reactSelectStyles<SubsidiarySearch>(isInvalid)}
                      />
                    );
                  }}
                </FormField>
              )}
            />
            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.type} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      inputId={labelId}
                      options={typesOf}
                      isDisabled={isLoading || isDisabled}
                      isLoading={isLoading}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      isClearable
                      styles={reactSelectStyles<TypeOf>(isInvalid)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="fileCsv"
              control={control}
              rules={{ required: true }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.fileCsv} error={fieldState.error}>
                  <FileUploader
                    isSubmitting={isLoading}
                    disabled={isLoading || isDisabled}
                    onChange={(f) => setValue("fileCsv", f)}
                    accept=".csv"
                  />
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default CostsImportForm;
